import { collectionGroup, query, where, orderBy } from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../libs/Firebase.js'
import { Subcollections } from '../../../pure-js/libs/Collections.js'
import { PrivateLesson } from '../../../pure-js/types/GrooverTypes.js'
import { useFirestoreQuery } from './useFirestoreQuery.js'
import { ReducerState } from './useFirestoreQueryHelper.js'
import { usePortalHostQueryGate } from './portalHostQueryGate'

/**
 * Hook to fetch private lessons for a specific enrollment user using reactive queries
 *
 * Uses collection group query to search across all clients' private_lessons subcollections.
 * Firestore rules will filter results to only show lessons for this enrollmentUserId.
 *
 * The returned PrivateLesson objects may contain either:
 * - New structure: instructor/location reference objects with id and name
 * - Legacy structure: instructorId/locationId scalar fields
 * - Both structures (during migration)
 *
 * Use helper functions from PrivateLessonHelpers to access instructor/location data:
 * - getInstructorId(lesson) - Gets instructor ID with fallback to legacy field
 * - getInstructorName(lesson) - Gets instructor name from reference object
 * - getLocationId(lesson) - Gets location ID with fallback to legacy field
 * - getLocationName(lesson) - Gets location name from reference object
 *
 * @param enrollmentUserId - The enrollment user ID from usePortalAuth, or null if not authenticated
 * @param clientId - Optional client to filter to (from useClientRouteHost()). When provided, adds
 *   a `where('clientId', '==', clientId)` filter — used by client-specific Portal subdomains (AC-1,
 *   COU-334). Requires a composite index (enrollmentUserId, clientId, startDate) on the
 *   private_lessons collection group; see firestore.indexes.json. Omitted/undefined for the unified
 *   portal, which shows all clients' data.
 * @returns ReducerState with status, data, error, and isLoading properties
 */
export const usePrivateLessonsForUser = (
  enrollmentUserId: string | null,
  clientId?: string
): ReducerState<PrivateLesson[]> => {
  const { readyUserId, hostLoading } = usePortalHostQueryGate(enrollmentUserId)

  const firestoreQuery = useMemo(() => {
    if (!readyUserId) {
      // Return dummy collection to avoid permission errors when user is not authenticated
      // This prevents Firestore from attempting to query with null/undefined values
      return collectionGroup(db, 'dummy-private-lessons')
    }

    // Use collection group query to search across all private_lessons subcollections
    // This matches the Firestore rule at line 154-162 which allows reading from collection group
    const constraints = [where('enrollmentUserId', '==', readyUserId)]
    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }
    return query(collectionGroup(db, Subcollections.PRIVATE_LESSONS), ...constraints, orderBy('startDate', 'desc'))
  }, [readyUserId, clientId])

  const result = useFirestoreQuery<PrivateLesson[]>(firestoreQuery, {
    fnName: 'usePrivateLessonsForUser',
    enabled: !!readyUserId
  })

  return {
    ...result,
    isLoading: hostLoading || result.isLoading,
    status: hostLoading ? 'loading' : result.status
  }
}
